import { Chess } from "@vendor/chess.js";
import type { ChessMove } from "./chessTypes.ts";

/** Material in pawns. The king is never won, so it counts for nothing here. */
export const PIECE_VALUES: Record<string, number> = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 0 };

const value = (type: string | undefined) => PIECE_VALUES[type || "p"] ?? 0;

// The exchange search makes thousands of trial moves per position, so it uses
// the vendored chess.js move generator directly: public moves() also writes
// SAN with check and mate marks for every move, which costs far more than the
// search itself. Only moves shown to the student are turned back into SAN.
type RawMove = {
  from: number;
  to: number;
  piece: string;
  captured?: string;
  promotion?: string;
};
type RawBoard = Chess & {
  _moves(opts?: { legal?: boolean; square?: string }): RawMove[];
  _makeMove(move: RawMove): void;
  _undoMove(): unknown;
  _isKingAttacked(color: string): boolean;
};

const raw = (board: Chess) => board as RawBoard;
const squareName = (index: number) => `${"abcdefgh"[index & 15]}${8 - (index >> 4)}`;
const promotionGain = (move: { promotion?: string }) =>
  move.promotion ? value(move.promotion) - 1 : 0;

/**
 * What the side to move wins on `square` by capturing with its cheapest legal
 * piece and letting the exchange run on, either side free to stop. Legal moves
 * respect pins and reveal pieces lined up behind the first capturer. The
 * board is changed and restored.
 */
function exchangeOn(board: RawBoard, square: number): number {
  const name = squareName(square);
  const target = board.get(name);
  if (!target || !board.isAttacked(name, board.turn())) return 0;
  let cheapest: RawMove | null = null;
  for (const move of board._moves({ legal: true })) {
    if (move.to === square && (!cheapest || value(move.piece) < value(cheapest.piece)))
      cheapest = move;
  }
  if (!cheapest) return 0;
  board._makeMove(cheapest);
  const reply = exchangeOn(board, square);
  board._undoMove();
  return Math.max(0, value(target.type) + promotionGain(cheapest) - reply);
}

function rawGain(board: RawBoard, move: RawMove): number {
  if (!move.captured && !move.promotion) return 0;
  board._makeMove(move);
  const reply = exchangeOn(board, move.to);
  board._undoMove();
  return value(move.captured) + promotionGain(move) - reply;
}

/**
 * The side to move's best winning capture, as a raw move and its gain,
 * optionally leaving out captures on one square.
 */
function rawBestCapture(board: RawBoard, skip?: number): { move: RawMove; gain: number } | null {
  let best: { move: RawMove; gain: number } | null = null;
  for (const move of board._moves({ legal: true })) {
    if (!move.captured || move.to === skip) continue;
    const gain = rawGain(board, move);
    // On equal gain a queen promotion is the one to name.
    const better =
      !best ||
      gain > best.gain ||
      (gain === best.gain && value(move.promotion) > value(best.move.promotion));
    if (gain > 0 && better) best = { move, gain };
  }
  return best;
}

/** Whether the side to move can deliver mate in one (only checking moves are tried). */
function rawMateInOne(board: RawBoard): boolean {
  const them = board.turn() === "w" ? "b" : "w";
  for (const move of board._moves({ legal: true })) {
    board._makeMove(move);
    const mate = board._isKingAttacked(them) && board._moves({ legal: true }).length === 0;
    board._undoMove();
    if (mate) return true;
  }
  return false;
}

function findRaw(board: RawBoard, move: { from: string; to: string; promotion?: string }) {
  return board
    ._moves({ legal: true, square: move.from })
    .find(
      (candidate) =>
        squareName(candidate.to) === move.to &&
        (candidate.promotion || undefined) === (move.promotion || undefined)
    );
}

function pretty(board: Chess, move: RawMove): ChessMove {
  const from = squareName(move.from);
  const to = squareName(move.to);
  return board
    .moves({ verbose: true, square: from })
    .find((m) => m.to === to && (m.promotion || undefined) === (move.promotion || undefined))!;
}

/** Net material the mover gains with this move once the replies on its square are counted. */
export function captureGain(board: Chess, move: ChessMove): number {
  const work = raw(new Chess(board.fen()));
  const found = findRaw(work, move);
  return found ? rawGain(work, found) : 0;
}

export type Capture = { move: ChessMove; gain: number };

/** Every capture for the side to move that wins material, best first. */
export function winningCaptures(board: Chess): Capture[] {
  const work = raw(new Chess(board.fen()));
  return work
    ._moves({ legal: true })
    .filter((move) => move.captured)
    .map((move) => ({ move, gain: rawGain(work, move) }))
    .filter((capture) => capture.gain > 0)
    .sort((a, b) => b.gain - a.gain)
    .map(({ move, gain }) => ({ move: pretty(work, move), gain }));
}

/** The capture that wins the most material for the side to move, if any wins something. */
export function bestCapture(board: Chess): Capture | null {
  const work = raw(new Chess(board.fen()));
  const best = rawBestCapture(work);
  return best ? { move: pretty(work, best.move), gain: best.gain } : null;
}

/**
 * The same position with the other side to move, as if the side to move
 * passed. Null when the side to move is in check (passing would be illegal).
 */
export function passedBoard(board: Chess): Chess | null {
  if (board.inCheck()) return null;
  const [placement, turn, castling, , halfmove, fullmove] = board.fen().split(" ");
  try {
    return new Chess(
      [placement, turn === "w" ? "b" : "w", castling, "-", halfmove, fullmove].join(" ")
    );
  } catch {
    return null;
  }
}

/** What the opponent threatens to win if the side to move ignores it. */
export function threatAgainstMover(board: Chess): Capture | null {
  const passed = passedBoard(board);
  return passed ? bestCapture(passed) : null;
}

/** Whether the side to move can deliver mate in one. */
export function hasMateInOne(board: Chess): boolean {
  return rawMateInOne(raw(new Chess(board.fen())));
}

/**
 * For each candidate move: what it wins, minus the best capture the opponent
 * then has, and whether it allows mate in one. One working board for all.
 * After a capture, taking back on the same square is already part of what
 * the capture wins, so the opponent either does that or captures elsewhere,
 * whichever is worse for the mover.
 */
export function moveSafety(
  board: Chess,
  moves: ChessMove[]
): Map<ChessMove, { net: number; allowsMate: boolean }> {
  const work = raw(new Chess(board.fen()));
  const result = new Map<ChessMove, { net: number; allowsMate: boolean }>();
  for (const move of moves) {
    const found = findRaw(work, move);
    if (!found) continue;
    const won = rawGain(work, found);
    const exchange = Boolean(found.captured || found.promotion);
    work._makeMove(found);
    const reply = rawBestCapture(work, exchange ? found.to : undefined)?.gain ?? 0;
    const allowsMate = rawMateInOne(work);
    work._undoMove();
    const net = exchange
      ? Math.min(won, (found.captured ? value(found.captured) : 0) + promotionGain(found) - reply)
      : won - reply;
    result.set(move, { net, allowsMate });
  }
  return result;
}
